/**
 * Session context. Fetches `GET /api/me` once on mount via TanStack Query and
 * exposes `{ user, contractor, contractors, loading }`. A 401 (mapped to a
 * thrown `PortalApiError` by the client) resolves to `user: null` rather than
 * an error state — that's the unauthenticated signal `RequireAuth` keys on.
 */

import type { PortalContractor, PortalUser, TenantRole } from '@portal/contracts'
import { useQuery } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react'
import { getMe } from '../api/auth'
import { PortalApiError } from '../api/portal-client'
import { meKey } from '../hooks/queryClient'
import { confirmRemembered } from './remembered-account'

export interface SessionContextValue {
  user: PortalUser | null
  contractor: PortalContractor | null
  contractors: PortalContractor[]
  /** Role in the active tenant. */
  role: TenantRole
  /** What the server allows in the active tenant — render only these. */
  permissions: string[]
  /** EVT operator: sees the Tenants page. */
  platformAdmin: boolean
  /** Signed in with a passkey or SSO (an e-mailed code is not enough to manage access). */
  strongAuth: boolean
  loading: boolean
  /** Force a re-fetch of `/api/me` (e.g. right after a successful login). */
  refresh: () => Promise<unknown>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: meKey,
    queryFn: getMe,
    // A 401 is an expected "not signed in" outcome — don't keep retrying it.
    retry: (_count, err) => !(err instanceof PortalApiError),
    staleTime: 30_000
  })

  // An SSO sign-in ends in the app, not on the sign-in page: the first signed-in `/api/me` confirms the account the
  // page remembered before redirecting (under the address the IdP actually signed in).
  const signedInEmail = query.data?.user?.email
  useEffect(() => {
    if (signedInEmail) confirmRemembered(signedInEmail)
  }, [signedInEmail])

  const value = useMemo<SessionContextValue>(() => {
    const me = query.data
    return {
      user: me?.user ?? null,
      contractor: me?.contractor ?? null,
      contractors: me?.contractors ?? [],
      role: me?.role ?? 'member',
      permissions: me?.permissions ?? [],
      platformAdmin: me?.platformAdmin === true,
      strongAuth: me?.strongAuth === true,
      loading: query.isLoading,
      refresh: () => query.refetch()
    }
  }, [query])

  return <SessionContext value={value}>{children}</SessionContext>
}

export function useSessionContext(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used within <AuthProvider>')
  return ctx
}
